import { asn1, pki } from 'node-forge';
import { arrayToPrivateKey, arrayToPublicKey, base64ToArray, exportPrivateCryptoKeyToPem, exportPublicCryptoKeyToPem, generateEncryptPair, RSA_ENCRYPTION_ALGO_PARAMS, } from '@lucidlink/ts-crypto/crypto';
import { webCrypto } from '@lucidlink/ts-crypto/web-crypto';
import { RSA_ENCRYPTION_OAEP_MAX_BYTES_DECRYPTED } from '#crypto/node-forge';
/**
 * Converts a PKCS#1 PEM (BEGIN RSA PRIVATE KEY) to PKCS#8 PEM (BEGIN PRIVATE KEY).
 * WebCrypto only supports PKCS#8 for private key import.
 */
const convertPkcs1ToPkcs8Pem = (pkcs1Pem) => {
    const forgePrivateKey = pki.privateKeyFromPem(pkcs1Pem);
    const pkcs8Asn1 = pki.wrapRsaPrivateKey(pki.privateKeyToAsn1(forgePrivateKey));
    const derBytes = asn1.toDer(pkcs8Asn1).getBytes();
    const b64 = btoa(derBytes);
    return `-----BEGIN PRIVATE KEY-----\n${b64}\n-----END PRIVATE KEY-----`;
};
const importPrivateKeyPem = (pem) => {
    let pkcs8Pem = pem;
    if (pem.startsWith('-----BEGIN RSA PRIVATE KEY-----')) {
        pkcs8Pem = convertPkcs1ToPkcs8Pem(pem);
    }
    const b64 = pkcs8Pem.replace(/-----[^-]+-----/g, '').replace(/\s+/g, '');
    return arrayToPrivateKey(base64ToArray(b64), RSA_ENCRYPTION_ALGO_PARAMS);
};
const importPublicKeyPem = (pem) => {
    const b64 = pem.replace(/-----[^-]+-----/g, '').replace(/\s+/g, '');
    return arrayToPublicKey(base64ToArray(b64), RSA_ENCRYPTION_ALGO_PARAMS);
};
export const createRsaCryptoAsync = async (params = {}) => {
    return WebCryptoRsa.create(params);
};
class WebCryptoRsa {
    publicKey;
    privateKey;
    constructor(publicKey, privateKey) {
        this.publicKey = publicKey;
        this.privateKey = privateKey;
    }
    static async create(params) {
        let publicKey;
        let privateKey;
        if (params.serializedPublicKey) {
            publicKey = await importPublicKeyPem(params.serializedPublicKey);
        }
        if (params.serializedPrivateKey) {
            privateKey = await importPrivateKeyPem(params.serializedPrivateKey);
        }
        if (!publicKey && !privateKey) {
            const pair = await generateEncryptPair();
            publicKey = pair.publicKey;
            privateKey = pair.privateKey;
        }
        return new WebCryptoRsa(publicKey, privateKey);
    }
    async serializePrivateKey() {
        return exportPrivateCryptoKeyToPem(this.privateKey);
    }
    async serializePublicKey() {
        return exportPublicCryptoKeyToPem(this.publicKey);
    }
    async encrypt(unencrypted) {
        const encrypted = await webCrypto.subtle.encrypt({ name: 'RSA-OAEP' }, this.publicKey, unencrypted);
        return new Uint8Array(encrypted);
    }
    async decrypt(encrypted) {
        const decrypted = await webCrypto.subtle.decrypt({ name: 'RSA-OAEP' }, this.privateKey, encrypted);
        return new Uint8Array(decrypted);
    }
    async encryptLong(unencrypted) {
        const result = [];
        let pos = 0;
        while (pos < unencrypted.length) {
            const segmentLength = Math.min(RSA_ENCRYPTION_OAEP_MAX_BYTES_DECRYPTED, unencrypted.length - pos);
            const segment = unencrypted.slice(pos, pos + segmentLength);
            result.push(await this.encrypt(segment));
            pos += segmentLength;
        }
        return result;
    }
    async decryptLong(encrypted) {
        const decryptedParts = [];
        for (const block of encrypted) {
            decryptedParts.push(new Uint8Array(await this.decrypt(block)));
        }
        const totalLength = decryptedParts.reduce((sum, part) => sum + part.length, 0);
        const result = new Uint8Array(totalLength);
        let offset = 0;
        for (const part of decryptedParts) {
            result.set(part, offset);
            offset += part.length;
        }
        return result;
    }
}
